import type { Reducer, UnknownAction } from "redux";
import {
    clearClientSession,
    getAuthenticationSession,
} from "./actions/authentication";

type SessionBoundState = {
    persistent?: unknown;
    user?: {
        id?: number;
    };
};

const RESET_ACTION = { type: "@@viridischem/SESSION_BOUNDARY_RESET" };

/**
 * Resets user/session/API state when the authenticated identity changes while
 * preserving the persisted subtree, whose user-owned slices are keyed by user.
 */
export const withSessionBoundary = <
    State extends SessionBoundState,
    Action extends UnknownAction,
    PreloadedState = State,
>(
    reducer: Reducer<State, Action, PreloadedState>
): Reducer<State, Action, PreloadedState> => {
    const resetEphemeralState = (
        state: State | undefined,
        action: Action
    ): State => {
        const initial = reducer(undefined, RESET_ACTION as unknown as Action);
        const base =
            state?.persistent === undefined
                ? initial
                : ({ ...initial, persistent: state.persistent } as State);
        return reducer(base, action);
    };

    return ((state, action) => {
        const currentState = state as State | undefined;

        if (action.type === clearClientSession.type) {
            return resetEphemeralState(currentState, action);
        }

        if (action.type === getAuthenticationSession.success.type) {
            const session = action.payload as {
                authenticated?: boolean;
                user?: { id?: number } | null;
            };
            const incomingUserId = session.authenticated
                ? (session.user?.id ?? null)
                : null;
            const currentUserId = currentState?.user?.id ?? null;

            if (incomingUserId !== currentUserId) {
                return resetEphemeralState(currentState, action);
            }
        }

        return reducer(state, action);
    }) as Reducer<State, Action, PreloadedState>;
};
